import { useMemo } from "react";
import { useTheme } from "@mui/material";

function statusChipFromTokens(t, status) {
  const s = String(status || "").toLowerCase();
  if (s === "pending" || s === "inactive") return { bgcolor: t.chipPendingBg, color: t.chipPendingColor };
  if (s === "approved" || s === "active" || s === "live") return { bgcolor: t.chipActiveBg, color: t.chipActiveColor };
  if (s === "rejected") return { bgcolor: t.chipRejectedBg, color: t.chipRejectedColor };
  return { bgcolor: t.chipDefaultBg, color: t.chipDefaultColor };
}

export function useAdminUi() {
  const theme = useTheme();
  const tokens = theme.admin?.tokens;

  return useMemo(() => {
    const t = tokens ?? {};
    const tableCardRadius = t.tableCardRadius ?? 10;

    const tableCardSx = {
      borderRadius: `${tableCardRadius}px`,
      border: `1px solid ${t.border}`,
      bgcolor: t.surface,
      overflow: "hidden",
      boxShadow: t.shadowSm,
    };

    const tableHeadRowSx = {
      "& .MuiTableCell-head": {
        fontSize: 12,
        fontWeight: 700,
        color: t.inkSoft,
        letterSpacing: "0.02em",
        textTransform: "uppercase",
        bgcolor: t.tableSectionHeadBg || t.tableHeadBg,
        borderBottom: `1px solid ${t.border}`,
        py: 1.1,
      },
    };

    return {
      ui: t,
      statusChipSx: (status) => statusChipFromTokens(t, status),
      detailLabelSx: { fontSize: 10, color: t.inkSoft, fontWeight: 500, mb: 0.25 },
      detailValueSx: { fontSize: 12.5, color: t.inkStrong || t.ink, lineHeight: 1.45 },
      detailMutedSx: { fontSize: 12, color: t.inkSoft },
      sectionLabelSx: {
        fontSize: 11,
        fontWeight: 700,
        color: t.inkSoft,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
      },
      /** Segmented tab track — responsive, flat (no gradient) */
      tabBarSx: {
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        flexWrap: "wrap",
        alignItems: "stretch",
        gap: 0.5,
        p: 0.5,
        borderRadius: "12px",
        bgcolor: t.tabTrackBg,
        border: `1px solid ${t.border}`,
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
      },
      tabBarItemSx: (active, variant = "neutral") => {
        const isPrimary = variant === "primary";
        const activeBg = isPrimary ? t.green : t.tabActiveBg;
        const activeColor = isPrimary ? "#fff" : t.tabActiveColor;
        return {
          textTransform: "none",
          fontSize: { xs: 12, sm: 13 },
          fontWeight: 600,
          lineHeight: 1.3,
          borderRadius: "10px",
          px: { xs: 1.25, sm: 2 },
          py: { xs: 0.85, sm: 0.75 },
          minHeight: 40,
          border: `1px solid ${active ? (isPrimary ? t.green : t.border) : "transparent"}`,
          bgcolor: active ? activeBg : "transparent",
          color: active ? activeColor : t.tabInactiveColor,
          boxShadow: active ? t.shadowSm : "none",
          transition: "background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease",
          whiteSpace: { xs: "normal", sm: "nowrap" },
          "&:hover": {
            bgcolor: active ? (isPrimary ? t.greenDark || t.green : t.tabActiveHoverBg) : t.filterHover,
            borderColor: active ? (isPrimary ? t.green : t.border) : t.borderLight,
          },
        };
      },
      profileTabSx: (active) => ({
        textTransform: "none",
        minWidth: { xs: 0, sm: 104 },
        flex: { xs: "1 1 0", sm: "0 1 auto" },
        fontSize: { xs: 12, sm: 13 },
        fontWeight: 600,
        borderRadius: "10px",
        px: { xs: 1.25, sm: 1.75 },
        py: { xs: 0.85, sm: 0.65 },
        minHeight: 40,
        borderColor: active ? t.tabActiveBg : "transparent",
        bgcolor: active ? t.tabActiveBg : "transparent",
        color: active ? t.tabActiveColor : t.tabInactiveColor,
        boxShadow: active ? t.shadowSm : "none",
        "&:hover": {
          bgcolor: active ? t.tabActiveHoverBg || t.tabActiveBg : t.filterHover,
        },
      }),
      subTabSx: (active) => ({
        textTransform: "none",
        minWidth: { xs: 0, sm: 104 },
        flex: { xs: "1 1 0", sm: "0 1 auto" },
        fontSize: { xs: 12, sm: 13 },
        fontWeight: 600,
        borderRadius: "10px",
        px: { xs: 1.25, sm: 1.75 },
        py: { xs: 0.85, sm: 0.65 },
        minHeight: 40,
        borderColor: active ? t.green : "transparent",
        bgcolor: active ? t.green : "transparent",
        color: active ? "#fff" : t.tabInactiveColor,
        boxShadow: active ? t.shadowSm : "none",
        "&:hover": {
          bgcolor: active ? t.greenDark || t.green : "rgba(87, 191, 120, 0.12)",
        },
      }),
      placeholderBoxSx: {
        border: `1px dashed ${t.placeholderBorder}`,
        borderRadius: 2,
        bgcolor: t.placeholderBg,
      },
      tableHeadRowSx,
      pageWrapSx: {
        px: { xs: 1.25, sm: 1.75, md: 2.5 },
        py: { xs: 1.25, sm: 1.75, md: 2.25 },
        maxWidth: "1680px",
        mx: "auto",
        width: "100%",
        boxSizing: "border-box",
        minHeight: "auto",
      },
      listPaperSx: { ...tableCardSx },
      tableCardSx,
      listHeaderSx: {
        p: { xs: 1.25, sm: 1.5 },
        borderBottom: `1px solid ${t.borderLight}`,
        bgcolor: t.listHeaderBg,
      },
      listTitleSx: {
        fontSize: { xs: 17, sm: 19 },
        fontWeight: 600,
        color: t.ink,
        letterSpacing: "-0.02em",
        lineHeight: 1.3,
      },
      listSubtitleSx: {
        fontSize: 12,
        color: t.inkSoft,
        mt: 0.35,
        lineHeight: 1.45,
      },
      tableHeaderCellSx: {
        fontSize: "0.75rem",
        fontWeight: 700,
        color: t.inkSoft,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        py: 1.25,
        px: 1.5,
        bgcolor: t.tableHeadBg,
        borderBottom: `1px solid ${t.border}`,
        whiteSpace: "nowrap",
        lineHeight: 1.4,
      },
      tableBodyCellSx: {
        fontSize: "0.875rem",
        color: t.inkMuted,
        py: 1.15,
        px: 1.5,
        lineHeight: 1.5,
        verticalAlign: "middle",
      },
      tableRowHoverSx: {
        "&:hover": { bgcolor: t.rowHover },
        "&:nth-of-type(even)": { bgcolor: t.rowStripe },
      },
      tableSx: {
        minWidth: 720,
        tableLayout: "auto",
      },
      tableContainerSx: {
        width: "100%",
        maxWidth: "100%",
        overflow: "auto",
        maxHeight: { xs: "calc(100vh - 260px)", sm: "calc(100vh - 280px)", lg: "calc(100vh - 300px)" },
        WebkitOverflowScrolling: "touch",
        "&::-webkit-scrollbar": { height: 8, width: 8 },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: t.scrollThumb || "rgba(0, 0, 0, 0.22)",
          borderRadius: 4,
        },
        "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
      },
      filterChipSx: (active) => ({
        height: 26,
        fontSize: 11,
        fontWeight: 600,
        cursor: "pointer",
        flexShrink: 0,
        letterSpacing: "normal",
        transition: "background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease",
        bgcolor: active ? t.gold : t.filterInactive,
        color: active ? t.tabActiveColor || "#fff" : t.inkMuted,
        border: active ? `1px solid ${t.gold}` : `1px solid ${t.borderLight}`,
        "&:hover": {
          bgcolor: active ? t.tabActiveHoverBg || "#c49a42" : t.filterHover,
        },
      }),
      primaryBtnSx: {
        textTransform: "none",
        fontSize: 13,
        fontWeight: 600,
        borderRadius: `${t.radiusSm ?? 8}px`,
        boxShadow: "none",
        px: 2,
        py: 0.65,
        flexShrink: 0,
        whiteSpace: "nowrap",
        letterSpacing: "normal",
        "&:hover": { boxShadow: "0 4px 14px rgba(87, 191, 120, 0.28)" },
      },
      statCardSx: (tone) => ({
        border: `1px solid ${t.border}`,
        borderRadius: `${tableCardRadius}px`,
        p: 1.35,
        width: { xs: "100%", sm: 210 },
        minWidth: 0,
        bgcolor: t.surface,
        transition: "transform 0.18s ease, box-shadow 0.18s ease",
        "&:hover": {
          boxShadow: t.shadowMd,
          transform: "translateY(-2px)",
        },
        "& .stat-icon": {
          width: 42,
          height: 42,
          borderRadius: 2,
          bgcolor: `${tone}18`,
          color: tone,
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
        },
      }),
      overviewMainCardSx: {
        borderRadius: 3,
        border: `1px solid ${t.border}`,
        overflow: "hidden",
        bgcolor: t.surface,
        boxShadow: t.shadowLg || t.shadowMd,
      },
      panelMutedBarSx: {
        px: { xs: 1.5, sm: 2.25 },
        py: 1.5,
        bgcolor: t.panelMuted,
        borderBottom: `1px solid ${t.borderLight}`,
      },
      panelSectionBarSx: {
        px: 2,
        py: 1.25,
        borderBottom: `1px solid ${t.borderLight}`,
        bgcolor: t.tableSectionHeadBg || t.panelMuted,
      },
    };
  }, [tokens]);
}
